import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DialogPropertyOpportunitiesComponent } from '@app/modules/properties/components/dialog-property-opportunities/dialog-property-opportunities.component';
import { DialogRemoveListingComponent } from '@app/modules/properties/components/dialog-remove-listing/dialog-remove-listing.component';
import { DialogSetLeadListingComponent } from '@app/modules/properties/components/dialog-set-lead-listing/dialog-set-lead-listing.component';
import { DialogUpdateListingListedStatusComponent } from '@app/modules/properties/components/dialog-update-listing-listed-status/dialog-update-listing-listed-status';
import { UsersFacade } from '@app/shared/+state/users';
import { NotificationType, User } from '@app/shared/interfaces';
import { IntercomService } from '@app/shared/services/intercom.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { Listing, PropertiesService } from '@app/shared/services/properties/properties.service';
import { DialogPropertyMutingService } from '@app/shared/services/property-muting/dialog-property-muting.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { filter, map } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-property-detail-merge-match',
  templateUrl: './property-detail-merge-match.component.html',
  styleUrls: ['./property-detail-merge-match.component.scss'],
})
export class PropertyDetailMergeMatchComponent implements OnInit {
  availableListings: { loading: boolean; data: Listing[] };
  numListOptionsForSearchBar = 10;
  searchCriteria: string;
  loading: boolean;
  saveStatus: number;
  deleting: boolean;
  syncRestricted = false;
  user: User = this.authService.getUserDetails();
  bookingComPropertyManagementEnabled = false;

  perms$ = this.userPermissionsService;
  Features = Features;

  public canUnmute$ = this.usersFacade.propertiesEntitlement$.pipe(
    filter((entitlement) => Boolean(entitlement)),
    map((entitlement) => {
      return entitlement.unlimited || Number(entitlement.value) > entitlement.current_value;
    })
  );

  @Input() property: any;

  @ViewChild('scheduleMenuTrigger')
  scheduleMenuTrigger: MatMenuTrigger;

  isCsDebug$ = this.internalCsService.debugMode;

  constructor(
    private readonly propertyService: PropertiesService,
    private toast: ToastNotificationsService,
    private readonly dialog: MatDialog,
    private readonly dialogPropertyMutingService: DialogPropertyMutingService,
    private readonly userPermissionsService: UserPermissionsService,
    private authService: AuthenticationService,
    private usersFacade: UsersFacade,
    private router: Router,
    private readonly intercomService: IntercomService,
    private optimizelyService: OptimizelyService,
    private readonly internalCsService: InternalCsService
  ) {}

  ngOnInit() {
    // make sure the user's entitlement data is up to date
    this.usersFacade.loadUser();
    this.propertyService.getListings().subscribe((res) => {
      this.loading = false;
      this.availableListings = {
        loading: false,
        data: res,
      };

      this.removeBadListingsFromActiveListings();
    });

    this.property.listings.map((listing) => {
      if (!listing.restrictions.available) {
        this.syncRestricted = true;
      }
      if (listing.restrictions.calendar_restricted) {
        this.syncRestricted = true;
      }
      if (listing.restrictions.is_pms_connected) {
        this.syncRestricted = true;
      }
      if (listing.restrictions.unclaimed) {
        this.syncRestricted = true;
      }
      if (listing.restrictions.missing_rate_plans) {
        this.syncRestricted = true;
      }
    });

    this.checkForBookingComPropertyManagement();
  }

  checkForBookingComPropertyManagement() {
    this.optimizelyService.bookingComPropertyManagementEnabled$.subscribe((flag) => {
      this.bookingComPropertyManagementEnabled = flag && this.propertyHasAirbnbListingAndNoBookingListing();
    });
  }

  removeBadListingsFromActiveListings() {
    // Filter out listings that we already have on this property
    if (this.property.listings && this.property.listings.length > 0) {
      // Remove listings which already belong to this property
      this.property.listings.forEach((propListing) => {
        const index = this.availableListings.data.findIndex(
          (listing) => listing.listing_id === propListing.listing_id && propListing.property.id === listing.property.id
        );

        if (index !== -1) {
          this.availableListings.data.splice(index, 1);
        }
      });
    }

    // Filter out listings which don't match the listing_types of our property
    const propType = this.property.types[0];

    this.availableListings.data.forEach((avListing, i) => {
      if (propType !== avListing.listing_type) {
        avListing.delete = true;
      }
    });

    this.availableListings.data = this.availableListings.data.filter((listing) => listing.delete !== true);
  }

  filtersMatchSearchCriteria(option, searchCriteria) {
    if (!searchCriteria || searchCriteria === '') {
      return true;
    }

    return option.name.toLowerCase().includes(searchCriteria.toLowerCase());
  }

  addListing(listing) {
    const payload = {
      listing_type: listing.platform_key,
      listing_id: listing.id,
    };

    this.propertyService.addListing(this.property.id, payload).subscribe((res) => {
      if (res.error) {
        this.toast.open(`Error adding listing: ${res.message}`, 'Dismiss', NotificationType.Error);
      } else {
        this.property.listings = res.data.listings;
        this.removeBadListingsFromActiveListings();
      }
    });
  }

  removeListing(listing) {
    if (this.property.listings.length < 2) {
      this.toast.open(`You cannot remove the last listing from a property`, 'Dismiss', NotificationType.Error);

      return;
    }

    const dialogRef = this.dialog.open(DialogRemoveListingComponent, {
      width: '430px',
      height: '300px',
      data: { listingName: listing.name },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const payload = {
          listing_type: listing.platform_key,
          listing_id: listing.id,
        };

        this.propertyService.removeListing(this.property.id, payload).subscribe((res) => {
          if (res.error) {
            this.toast.open(`Error removing listing ${res.message}`, 'Dismiss', NotificationType.Error);
          } else {
            const index = this.property.listings.findIndex((list) => list.id === listing.id);

            if (index !== -1) {
              this.availableListings.data.push(this.property.listings[index]);
              this.property.listings = res.data.listings;
            }
          }
        });
      }
    });
  }

  updateProperty() {
    this.saveStatus = 1;

    this.propertyService.updateProperty(this.property.id, this.property).subscribe((res) => {
      if (res.error) {
        this.saveStatus = -1;
      } else {
        this.saveStatus = 2;
      }
    });
  }

  muteProperty(mode: 'mute' | 'unmute') {
    this.dialogPropertyMutingService.openMutingDialog(mode, this.property.id).subscribe((res) => {
      if ('muted' in res) {
        this.property.muted = res.muted;
      }
    });
  }

  resetCalendar() {
    this.propertyService.resetCalendarAccess(this.property.id).subscribe((res: string) => {
      if (res) {
        this.property.ical_feed = res;

        this.toast.open(`The iCal feed access has been reset`, 'Dismiss', NotificationType.Success);
      }
    });
  }

  copiedToClipboard() {
    this.toast.open(`The iCal link has been copied to your clipboard`, 'Dismiss', NotificationType.Success, 2000);
  }

  syncStatusChange(newValue: MatSlideToggleChange) {
    this.property.sync = newValue.checked;
    this.updateProperty();
  }

  showLeadListingDialog(listing: Listing) {
    const dialogRef = this.dialog.open(DialogSetLeadListingComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      disableClose: true,
      data: {
        listing,
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        // clear all other lead listings
        this.property.listings.map((listing) => {
          listing.is_lead_listing = false;
        });

        // Set this listing to the lead
        listing.is_lead_listing = true;
      }
    });
  }

  updateListingStatus(listing: Listing) {
    const dialogRef = this.dialog.open(DialogUpdateListingListedStatusComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      disableClose: true,
      data: {
        status: !listing.restrictions.available,
        listing,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Update the local listing status
        listing.restrictions.available = !listing.restrictions.available;
        // Refresh sync restriction status
        this.updateSyncRestrictions();
      }
    });
  }

  private updateSyncRestrictions() {
    this.syncRestricted = this.property.listings.some(
      (listing) =>
        !listing.restrictions.available ||
        listing.restrictions.calendar_restricted ||
        listing.restrictions.is_pms_connected ||
        listing.restrictions.unclaimed ||
        listing.restrictions.missing_rate_plans
    );
  }

  propertyHasAirbnbListingAndNoBookingListing(): boolean {
    if (!this.property?.listings?.length) return false;

    const hasAirbnbListing = this.property.listings.some(
      (listing) => listing.platform_key.toLowerCase() === 'airbnb-official' && listing.claimed === true
    );

    const hasBookingListing = this.property.listings.some(
      (listing) => listing.platform_key.toLowerCase() === 'booking'
    );

    return hasAirbnbListing && !hasBookingListing;
  }

  openCreateBookingDialog(): void {
    this.router.navigate([`/properties/property/${this.property.id}/create-booking-listing`]);
  }

  openIntercomHelp(articleId: string) {
    this.intercomService.openHelpArticle(articleId);
  }

  get hasAirbnbListings(): boolean {
    return this.property?.listings?.some((listing) => listing.platform.toLowerCase() === 'airbnb') ?? false;
  }

  openOpportunities() {
    if (!this.hasAirbnbListings) {
      return;
    }

    this.dialog
      .open(DialogPropertyOpportunitiesComponent, {
        data: {
          propertyId: this.property.id,
        },
        width: '100%',
        maxWidth: '600px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe();
  }
}
